// src/catalogVersion.ts
import { isOfferableType } from "@rock/types";
function recallCatalogTypes(types) {
  return types.filter((type) => type.scope !== "supplies" && type.scope !== "system" && isOfferableType(type));
}
function recallCatalogVersion(types, topics) {
  return catalogVersionOf(
    recallCatalogTypes(types).map((type) => type.id),
    topics.map((topic) => topic.id)
  );
}
function catalogVersionOf(typeIds, topicIds = []) {
  const ids = [...[...typeIds].sort(), ...[...topicIds].sort().map((id) => `topic:${id}`)];
  const text = ids.join("\n");
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

// src/classification.ts
import { slugify } from "@rock/util";
function normalizeProductKind(raw) {
  return slugify(raw, "unknown");
}
function planClassificationEdit(current, edit) {
  const typeIds = [...new Set(edit.typeIds)];
  const removed = current.typeIds.filter((id) => !typeIds.includes(id));
  const ruledOut = [...current.ruledOutTypeIds ?? [], ...removed].filter((id) => !typeIds.includes(id));
  return typePlan(current, { ...edit, typeIds, ruledOutTypeIds: [...new Set(ruledOut)], adminEdited: true });
}
function planTypeRuleOut(current, typeId) {
  return typePlan(current, {
    typeIds: current.typeIds.filter((id) => id !== typeId),
    productKind: current.productKind,
    ruledOutTypeIds: [.../* @__PURE__ */ new Set([...current.ruledOutTypeIds ?? [], typeId])],
    adminEdited: !!current.adminEdited
  });
}
function planRulingLift(current, typeId) {
  return typePlan(current, {
    typeIds: current.typeIds,
    productKind: current.productKind,
    ruledOutTypeIds: (current.ruledOutTypeIds ?? []).filter((id) => id !== typeId),
    adminEdited: !!current.adminEdited
  });
}
function planTopicsEdit(current, edit) {
  const topicIds = [...new Set(edit)];
  const removed = current.topicIds.filter((id) => !topicIds.includes(id));
  const ruledOut = [...current.ruledOutTopicIds ?? [], ...removed].filter((id) => !topicIds.includes(id));
  return topicPlan(current, { topicIds, ruledOutTopicIds: [...new Set(ruledOut)], topicsAdminEdited: true });
}
function planTopicRuleOut(current, topicId) {
  return topicPlan(current, {
    topicIds: current.topicIds.filter((id) => id !== topicId),
    ruledOutTopicIds: [.../* @__PURE__ */ new Set([...current.ruledOutTopicIds ?? [], topicId])],
    topicsAdminEdited: !!current.topicsAdminEdited
  });
}
function planTopicRulingLift(current, topicId) {
  return topicPlan(current, {
    topicIds: current.topicIds,
    ruledOutTopicIds: (current.ruledOutTopicIds ?? []).filter((id) => id !== topicId),
    topicsAdminEdited: !!current.topicsAdminEdited
  });
}
function reconcileClassification(current, fresh, { keepLists = false } = {}) {
  const ruledOut = current?.ruledOutTypeIds ?? [];
  const ruledOutTopics = current?.ruledOutTopicIds ?? [];
  const typed = current && (current.adminEdited || keepLists) ? {
    ...fresh,
    typeIds: current.typeIds,
    productKind: current.productKind,
    adminEdited: !!current.adminEdited
  } : fresh;
  const answer = current && (current.topicsAdminEdited || keepLists) ? { ...typed, topicIds: current.topicIds, topicsAdminEdited: current.topicsAdminEdited } : typed;
  const kept = {
    ...answer,
    ...current?.headlineAdminEdited ? { headline: current.headline, headlineAdminEdited: true } : {},
    typeIds: answer.typeIds.filter((id) => !ruledOut.includes(id)),
    topicIds: answer.topicIds.filter((id) => !ruledOutTopics.includes(id)),
    ...ruledOut.length ? { ruledOutTypeIds: ruledOut } : {},
    ...ruledOutTopics.length ? { ruledOutTopicIds: ruledOutTopics } : {}
  };
  return withSuggestions(kept, fresh);
}
function withSuggestions(kept, fresh) {
  const ruledOut = kept.ruledOutTypeIds ?? [];
  const ruledOutTopics = kept.ruledOutTopicIds ?? [];
  const suggestedTypeIds = fresh.typeIds.filter((id) => !kept.typeIds.includes(id) && !ruledOut.includes(id));
  const suggestedTopicIds = fresh.topicIds.filter(
    (id) => !kept.topicIds.includes(id) && !ruledOutTopics.includes(id)
  );
  if (!suggestedTypeIds.length && !suggestedTopicIds.length) return kept;
  return {
    ...kept,
    ...suggestedTypeIds.length ? { suggestedTypeIds } : {},
    ...suggestedTopicIds.length ? { suggestedTopicIds } : {},
    hasSuggestions: true
  };
}
function typePlan(current, plan) {
  const suggestedTypeIds = (current.suggestedTypeIds ?? []).filter(
    (id) => !plan.typeIds.includes(id) && !plan.ruledOutTypeIds.includes(id)
  );
  const hasSuggestions = suggestedTypeIds.length > 0 || (current.suggestedTopicIds ?? []).length > 0;
  return { ...plan, suggestedTypeIds, hasSuggestions };
}
function topicPlan(current, plan) {
  const suggestedTopicIds = (current.suggestedTopicIds ?? []).filter(
    (id) => !plan.topicIds.includes(id) && !plan.ruledOutTopicIds.includes(id)
  );
  const hasSuggestions = suggestedTopicIds.length > 0 || (current.suggestedTypeIds ?? []).length > 0;
  return { ...plan, suggestedTopicIds, hasSuggestions };
}

// src/coverage.ts
import { EQUIPMENT_CORE_FIELD_KEYS } from "@rock/types";
import { equipmentModelNumber } from "@rock/util";
function recallCoverageTier({ equipment, brandId }) {
  const hidden = equipment.hiddenCaptureFields ?? [];
  if (equipment.details?.modelId || brandId && equipmentModelNumber(equipment)) return "model";
  if (brandId) return hidden.includes(EQUIPMENT_CORE_FIELD_KEYS.MODEL_NUMBER) ? "ignored" : "brand-only";
  return hidden.includes(EQUIPMENT_CORE_FIELD_KEYS.BRAND) ? "ignored" : "no-brand";
}
function recallCoverageOf(units) {
  const coverage = { model: [], brandOnly: [], noBrand: [], ignored: [], counted: 0 };
  for (const unit of units) {
    const id = unit.equipment.id;
    switch (recallCoverageTier(unit)) {
      case "model":
        coverage.model.push(id);
        break;
      case "brand-only":
        coverage.brandOnly.push(id);
        break;
      case "no-brand":
        coverage.noBrand.push(id);
        break;
      case "ignored":
        coverage.ignored.push(id);
        break;
    }
  }
  coverage.counted = coverage.model.length + coverage.brandOnly.length + coverage.noBrand.length;
  return coverage;
}
function recallCoverageGap(unit) {
  const tier = recallCoverageTier({ ...unit, equipment: { ...unit.equipment, hiddenCaptureFields: [] } });
  if (tier === "brand-only") return EQUIPMENT_CORE_FIELD_KEYS.MODEL_NUMBER;
  if (tier === "no-brand") return EQUIPMENT_CORE_FIELD_KEYS.BRAND;
  return void 0;
}

// src/identifiers.ts
import { identifierStrengthOf, normalizeIdentifier as normalizeIdentifier2 } from "@rock/util";

// src/notice.ts
import { normalizeIdentifier } from "@rock/util";
function formatNotice(input) {
  return [
    `TITLE: ${input.title}`,
    `DATE: ${input.recallDate}`,
    `PRODUCTS: ${JSON.stringify(input.products)}`,
    `MANUFACTURERS: ${JSON.stringify(input.manufacturers)}`,
    `DESCRIPTION: ${input.description}`,
    ...input.pageTables ? [`PAGE TABLES: ${input.pageTables}`] : []
  ].join("\n");
}
var NOTICE_SECTION_KEYS = [
  "TITLE",
  "DATE",
  "PRODUCTS",
  "MANUFACTURERS",
  "DESCRIPTION",
  "PAGE TABLES"
];
var OPTIONAL_SECTIONS = /* @__PURE__ */ new Set(["PAGE TABLES"]);
function noticeSections(noticeText) {
  const starts = [];
  let cursor = 0;
  for (const [index, key] of NOTICE_SECTION_KEYS.entries()) {
    const label = `${index === 0 ? "" : "\n"}${key}: `;
    const labelAt = index === 0 ? noticeText.startsWith(label) ? 0 : -1 : noticeText.indexOf(label, cursor);
    if (labelAt < 0 && OPTIONAL_SECTIONS.has(key)) continue;
    if (labelAt < 0) return [];
    cursor = labelAt + label.length;
    starts.push({ key, labelAt, start: cursor });
  }
  return starts.map(({ key, start }, index) => {
    const end = starts[index + 1]?.labelAt ?? noticeText.length;
    const items = key === "PRODUCTS" || key === "MANUFACTURERS" ? listItems(noticeText, start, end) : void 0;
    return { key, start, end, ...items ? { items } : {} };
  });
}
function listItems(noticeText, start, end) {
  let parsed;
  try {
    parsed = JSON.parse(noticeText.slice(start, end));
  } catch {
    return void 0;
  }
  if (!Array.isArray(parsed) || !parsed.every((item) => typeof item === "string")) return void 0;
  const items = [];
  let cursor = start;
  for (const item of parsed) {
    const literal = JSON.stringify(item);
    const at = noticeText.indexOf(literal, cursor);
    if (at < 0 || at >= end) return void 0;
    items.push({ start: at + 1, end: at + literal.length - 1 });
    cursor = at + literal.length;
  }
  return items;
}
var CELL_LINE_SEPARATOR = " \xB7 ";
var CONFLICT_SEPARATOR = " / ";
function tokenizeNotice(text) {
  const tokens = [];
  const words = /\S+/g;
  let boundary = false;
  for (let match = words.exec(text); match; match = words.exec(text)) {
    const folded = normalizeIdentifier(match[0]);
    if (!folded) {
      boundary ||= isBoundaryWord(match[0]);
      continue;
    }
    const token = { raw: match[0], folded, start: match.index, end: match.index + match[0].length };
    tokens.push(boundary ? { ...token, afterBoundary: true } : token);
    boundary = false;
  }
  return tokens;
}
function isBoundaryWord(word) {
  return /^\\?\|/.test(word) || word === CELL_LINE_SEPARATOR.trim() || word === CONFLICT_SEPARATOR.trim();
}
var MAX_TOKEN_RUN = 4;
function locateIdentifier(tokens, folded) {
  if (!folded) return [];
  const spans = [];
  for (let i = 0; i < tokens.length; i++) {
    let run = "";
    for (let j = i; j < tokens.length && j - i < MAX_TOKEN_RUN; j++) {
      if (j > i && tokens[j].afterBoundary) break;
      run += tokens[j].folded;
      if (run.length > folded.length) break;
      if (run === folded) {
        spans.push({ start: tokens[i].start, end: tokens[j].end });
        break;
      }
    }
  }
  return spans;
}

// src/identifiers.ts
var MIN_PREFIX_LENGTH = 2;
function foldIdentifier(value) {
  return normalizeIdentifier2(value);
}
function ruleEnds(value) {
  return value.split("..").map((end) => foldIdentifier(end)).filter((end) => end.length > 0);
}
function exactIdentifiers(rule) {
  return exactHalves(rule).map(foldIdentifier).filter((id) => id.length > 0);
}
function listedPrefixOf(rule, folded) {
  if (rule.kind !== "exact" || exactIdentifiers(rule).includes(folded)) return void 0;
  const extended = exactHalves(rule).map((half) => ({ half, fold: foldIdentifier(half) })).filter(({ fold }) => extendsIdentifier(folded, fold));
  return extended.sort((a, b) => b.fold.length - a.fold.length)[0]?.half;
}
function exactHalves(rule) {
  return rule.value.split(/\s+\/\s+/).map((half) => half.trim());
}
function extendsIdentifier(folded, listed) {
  return listed.length >= MIN_PREFIX_LENGTH && folded.length > listed.length && folded.startsWith(listed);
}
function modelRuleIdentifiers(rule) {
  if (rule.kind === "any") return [];
  return rule.kind === "exact" ? exactIdentifiers(rule) : ruleEnds(rule.value);
}
function describeModelRule(rule) {
  switch (rule.kind) {
    case "any":
      return "every model";
    case "exact":
      return rule.value;
    default:
      return `${rule.kind}: ${rule.value}`;
  }
}
function describeMatchedRule(match) {
  return match.prefixMatch ? `starts with ${match.prefixMatch.listed}` : describeModelRule(match.rule);
}
function identifierStrength(rule, matched) {
  if (rule.kind !== "exact") return "weak";
  return identifierStrengthOf(matched ?? foldIdentifier(rule.value));
}
function identifierInNotice(noticeText, value) {
  const folded = foldIdentifier(value);
  return folded.length > 0 && locateIdentifier(tokenizeNotice(noticeText), folded).length > 0;
}
function suppressedInNotice(noticeText, rules) {
  const tokens = tokenizeNotice(noticeText);
  return rules.suppressed.filter((folded) => locateIdentifier(tokens, folded).length > 0);
}
function isRuleActive(rule) {
  return rule.verified || !!rule.accepted;
}
function scopeValues(rule, modelNumbers = []) {
  const own = modelNumbers.map(foldIdentifier);
  return [.../* @__PURE__ */ new Set([foldIdentifier(rule.value), ...matchedHalves(rule, own), ...own])].filter(Boolean);
}
function matchedHalves(rule, own) {
  const halves = rule.kind === "exact" ? exactIdentifiers(rule) : [];
  if (!own.length) return halves;
  const equal = halves.filter((half) => own.includes(half));
  return equal.length ? equal : halves.filter((half) => own.some((folded) => extendsIdentifier(folded, half)));
}
function unitScope(rule, modelNumbers) {
  return rule.kind === "any" && modelNumbers.length === 0 ? void 0 : scopeValues(rule, modelNumbers);
}
function scopeCovers(scoped, values) {
  if (scoped.appliesToModels.length === 0) return true;
  return scoped.appliesToModels.some((model) => values.includes(foldIdentifier(model)));
}
function conditionBinds(scoped, rules, scope) {
  if (!scope || scoped.appliesToModels.length === 0) return true;
  const named = scoped.appliesToModels.some((model) => namesModel(model, rules.modelRules));
  return !named || scopeCovers(scoped, scope);
}
function namesModel(value, modelRules) {
  const folded = foldIdentifier(value);
  return modelRules.some(
    (rule) => rule.kind !== "any" && (foldIdentifier(rule.value) === folded || ruleNames(rule, folded))
  );
}
function scopedSerialRules(rules, scope) {
  return rules.serialRules.filter((rule) => conditionBinds(rule, rules, scope));
}
function applicableChecks(rules, scope) {
  return rules.checks.filter((check) => conditionBinds(check, rules, scope));
}
function ruleNames(rule, folded) {
  switch (rule.kind) {
    case "any":
      return true;
    case "exact":
      return exactIdentifiers(rule).includes(folded);
    case "prefix":
      return ruleEnds(rule.value).some(
        (prefix) => prefix.length >= MIN_PREFIX_LENGTH && folded.startsWith(prefix)
      );
    case "range":
      return isBetween(folded, ruleEnds(rule.value));
  }
}
var YEAR = /^(19|20)\d{2}$/;
var LONG_NUMBER = /^\d{10,}$/;
var EDGE_PUNCTUATION = /^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu;
function harvestIdentifierTokens(noticeText) {
  const seen = /* @__PURE__ */ new Map();
  for (const token of tokenizeNotice(noticeText)) {
    const { folded, raw } = token;
    if (folded.length < 4 || !/\d/.test(folded)) continue;
    if (YEAR.test(folded) || LONG_NUMBER.test(folded)) continue;
    if (/[$%]/.test(raw) || /^\d{1,2}:\d{2}/.test(raw) || /^\d+(st|nd|rd|th)$/i.test(raw)) continue;
    if (/^\d+(\.\d+)?(x|mm|cm|in|lb|lbs|kg|oz|ft|v|w|a|hz|btu|gal)$/i.test(folded)) continue;
    if (!seen.has(folded)) seen.set(folded, raw.replace(EDGE_PUNCTUATION, "") || folded);
  }
  return [...seen.values()];
}
function verifyRecallRules(noticeText, rules) {
  const tokens = tokenizeNotice(noticeText);
  const present = (folded) => locateIdentifier(tokens, folded).length > 0;
  const modelRules = rules.modelRules.map((rule) => ({
    ...rule,
    verified: rule.kind !== "any" && modelRuleIdentifiers(rule).every(present)
  }));
  const serialRules = rules.serialRules.map((rule) => ({
    ...rule,
    verified: serialEnds(rule.value).some(present)
  }));
  const unverified = [...modelRules, ...serialRules].filter((rule) => !rule.verified && rule.source !== "admin").map((rule) => foldIdentifier(rule.value));
  const covered = coveredIdentifiers({ ...rules, modelRules, serialRules }, tokens);
  const harvestedNotExtracted = harvestIdentifierTokens(noticeText).filter((token) => {
    const folded = foldIdentifier(token);
    return !covered.some((cover) => cover.covers(folded)) && !rules.suppressed.includes(folded);
  });
  return {
    rules: { ...rules, modelRules, serialRules },
    verification: { unverified: dedupe(unverified), harvestedNotExtracted }
  };
}
function serialEnds(value) {
  return value.split(/\.\.|\bto\b|\bthrough\b/i).map((end) => foldIdentifier(end)).filter((end) => end.length >= 2);
}
function maskedSerialEnds(value, masks = "placeholders") {
  const ends = [];
  for (const piece of value.split(/\.\.|\bto\b|\bthrough\b/i)) {
    const trimmed2 = piece.replace(/^[#\s]+/, "").replace(/[.,;:\s]+$/, "");
    const [, body = "", mask = ""] = /^([\s\S]*?)([*#?_\s]*)$/.exec(trimmed2) ?? [];
    if (/[*#?]/.test(body)) return void 0;
    const placeholders = masks === "drop" ? "" : "X".repeat(mask.replace(/\s/g, "").length);
    const end = foldIdentifier(body) + placeholders;
    if (end) ends.push(end);
  }
  return ends;
}
function maskStart(value) {
  const mask = /X+$/.exec(value);
  return mask && mask.index > 0 ? mask.index : void 0;
}
function coveredIdentifiers(rules, tokens) {
  const covers = [];
  const literal = (values) => {
    const pieces = new Set(values);
    for (const value of values) {
      for (const span of locateIdentifier(tokens, value)) {
        for (const token of tokens) {
          if (token.start >= span.start && token.end <= span.end) pieces.add(token.folded);
        }
      }
    }
    covers.push({ covers: (token) => pieces.has(token) });
  };
  for (const rule of rules.modelRules) {
    if (rule.kind === "any") continue;
    if (rule.kind === "exact") literal(exactIdentifiers(rule));
    else if (rule.kind === "prefix") {
      const ends = ruleEnds(rule.value);
      covers.push({ covers: (token) => ends.some((p) => token.startsWith(p)) });
    } else {
      const ends = ruleEnds(rule.value);
      covers.push({ covers: (token) => ends.includes(token) || isBetween(token, ends) });
    }
  }
  literal(rules.serialRules.flatMap((rule) => serialEnds(rule.value)));
  literal(rules.upcs.map((upc) => foldIdentifier(upc.value)));
  const checked = new Set(rules.checks.flatMap(checkNumbers));
  covers.push({ covers: (token) => checked.has(token) });
  return covers;
}
function checkNumbers(check) {
  return tokenizeNotice(check.text).map((token) => token.folded).filter((folded) => folded.length >= 3 && /^\d+$/.test(folded));
}
function isBetween(value, ends) {
  const [start, end] = ends;
  if (!start || !end || start.length !== end.length || value.length !== start.length) return false;
  return value >= start && value <= end;
}
function dedupe(values) {
  return [...new Set(values)];
}

// src/tables.ts
import { normalizeIdentifier as normalizeIdentifier3 } from "@rock/util";
function placeTables(description, tables) {
  const normalized = normalizeWithIndex(description);
  const splices = [];
  const appended = [];
  let cursor = 0;
  const cleaned = tables.map(withoutPhotoColumns);
  for (const table of cleaned) {
    const found = locateTable(table, description, normalized, cursor);
    if (found) {
      const rows = tableRows(table, (cell) => found.cellText.get(cell) ?? "");
      splices.push({ start: found.start, end: found.end, markdown: toMarkdown(rows) });
      cursor = found.normalizedEnd;
    } else {
      appended.push(...pageTableMarkdown(table));
    }
  }
  const reshaped = splice(description, splices);
  if (keepsEveryWord(description, reshaped)) return { description: reshaped, pageTables: appended.join("\n\n") };
  return { description, pageTables: cleaned.flatMap(pageTableMarkdown).join("\n\n") };
}
function noticeBlocks(noticeText, range) {
  const blocks = [];
  const lines = linesOf(noticeText, range);
  let textStart = range.start;
  let i = 0;
  while (i < lines.length) {
    const table = tableLinesAt(lines, i);
    if (!table) {
      i++;
      continue;
    }
    pushText(blocks, noticeText, textStart, lines[i].start);
    const [header, , ...body] = table;
    const end = table[table.length - 1].end;
    blocks.push({
      kind: "table",
      start: lines[i].start,
      end,
      rows: [header, ...body].map(cellRanges(noticeText))
    });
    textStart = end;
    i += table.length;
  }
  pushText(blocks, noticeText, textStart, range.end);
  return blocks;
}
var MIN_ROWS_FOR_PHOTO_COLUMNS = 3;
function withoutPhotoColumns(table) {
  if (table.length < MIN_ROWS_FOR_PHOTO_COLUMNS) return table;
  const placed = placeCells(table);
  const filled = /* @__PURE__ */ new Set();
  for (const { cell, row, column } of placed) {
    if (row + cell.rowspan < 2 || cellLines(cell).length === 0) continue;
    for (let c = column; c < column + cell.colspan; c++) filled.add(c);
  }
  const spansFilled = ({ cell, column }) => Array.from({ length: cell.colspan }, (_, i) => column + i).some((c) => filled.has(c));
  return table.map((_, row) => placed.filter((p) => p.row === row && spansFilled(p)).map((p) => p.cell));
}
function locateTable(table, description, normalized, cursor) {
  const units = table.flat().flatMap((cell) => cellLines(cell).map((line) => ({ cell, line })));
  const joined = units.map((unit) => unit.line).join(" ");
  if (!joined) return void 0;
  const at = normalized.text.indexOf(joined, cursor);
  if (at < 0) return alignTable(units, description, normalized, cursor);
  if (normalized.text.indexOf(joined, at + 1) >= 0) return void 0;
  const original = (from, length) => description.slice(normalized.index[from], normalized.index[from + length - 1] + 1);
  const lines = /* @__PURE__ */ new Map();
  let pos = at;
  for (const { cell, line } of units) {
    lines.set(cell, [...lines.get(cell) ?? [], original(pos, line.length).replace(/\s+/g, " ")]);
    pos += line.length + 1;
  }
  return {
    start: normalized.index[at],
    end: normalized.index[at + joined.length - 1] + 1,
    normalizedEnd: at + joined.length,
    cellText: new Map([...lines].map(([cell, text]) => [cell, text.join(CELL_LINE_SEPARATOR)]))
  };
}
var MAX_DIFFERING_CELL_SHARE = 0.25;
var MAX_ALIGNMENT_SIZE = 4e6;
function alignTable(units, description, normalized, cursor) {
  const descriptionWords = wordsOf(normalized.text, cursor);
  const pageWords = units.flatMap((unit, lineIndex) => wordsOf(unit.line).map((word) => ({ ...word, lineIndex })));
  if (!descriptionWords.length || pageWords.length * descriptionWords.length > MAX_ALIGNMENT_SIZE) return void 0;
  const aligned = alignWords(pageWords, descriptionWords);
  if (!aligned.length) return void 0;
  const lineWords = units.map(() => []);
  for (const { word, lineIndex } of aligned) lineWords[lineIndex].push(descriptionWords[word]);
  const original = (word) => description.slice(normalized.index[word.start], normalized.index[word.end - 1] + 1);
  const cells = alignedCells(units.map((unit, i) => alignedLine(unit, lineWords[i].map(original))));
  const differing = cells.filter((cell) => cell.differs).length;
  if (differing > cells.length * MAX_DIFFERING_CELL_SHARE || tradesText(cells)) return void 0;
  const first = descriptionWords[aligned[0].word];
  const last = descriptionWords[aligned[aligned.length - 1].word];
  return {
    start: normalized.index[first.start],
    end: normalized.index[last.end - 1] + 1,
    normalizedEnd: last.end,
    cellText: new Map(
      cells.map((cell) => [cell.cell, cell.lines.map((line) => line.text).join(CELL_LINE_SEPARATOR)])
    )
  };
}
function wordsOf(text, from = 0) {
  return [...text.slice(from).matchAll(/\S+/g)].map((match) => ({
    text: match[0],
    folded: normalizeIdentifier3(match[0]),
    start: from + match.index,
    end: from + match.index + match[0].length
  }));
}
var EDIT_COST = 2;
function alignWords(page, descriptionWords) {
  const width = descriptionWords.length + 1;
  const cost = new Int32Array((page.length + 1) * width);
  const at = (i, j2) => cost[i * width + j2];
  const keepCost = (i, j2) => matchCost(page[i - 1], descriptionWords[j2 - 1]);
  for (let i = 1; i <= page.length; i++) {
    cost[i * width] = i * EDIT_COST;
    for (let j2 = 1; j2 < width; j2++) {
      const kept = at(i - 1, j2 - 1) + keepCost(i, j2);
      cost[i * width + j2] = Math.min(kept, at(i - 1, j2) + EDIT_COST, at(i, j2 - 1) + EDIT_COST);
    }
  }
  let j = 0;
  for (let k = 1; k < width; k++) if (at(page.length, k) < at(page.length, j)) j = k;
  const aligned = [];
  for (let i = page.length; i > 0; ) {
    const here = at(i, j);
    const keep = j > 0 ? keepCost(i, j) : EDIT_COST;
    const kept = j > 0 && here === at(i - 1, j - 1) + keep;
    const added = j > 0 && here === at(i, j - 1) + EDIT_COST;
    const leftOut = here === at(i - 1, j) + EDIT_COST;
    if (kept && (keep < EDIT_COST || !(i === 1 && leftOut))) {
      i--;
      j--;
      aligned.push({ word: j, lineIndex: page[i].lineIndex });
    } else if (added) {
      j--;
      aligned.push({ word: j, lineIndex: page[i - 1].lineIndex });
    } else i--;
  }
  return aligned.reverse();
}
function matchCost(a, b) {
  if (a.text === b.text || a.folded !== "" && a.folded === b.folded) return 0;
  return nearlySame(a.folded, b.folded) ? EDIT_COST / 2 : EDIT_COST;
}
var MIN_NEAR_MATCH_LENGTH = 3;
function nearlySame(a, b) {
  if (a.length < MIN_NEAR_MATCH_LENGTH || b.length < MIN_NEAR_MATCH_LENGTH) return false;
  return a.startsWith(b) || b.startsWith(a) || oneEditApart(a, b);
}
function oneEditApart(a, b) {
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  while (i < a.length && a[i] === b[i]) i++;
  if (a.length === b.length) return a.slice(i + 1) === b.slice(i + 1);
  const [longer, shorter] = a.length > b.length ? [a, b] : [b, a];
  return longer.slice(i + 1) === shorter.slice(i);
}
function alignedLine({ cell, line }, words) {
  const description = words.join(" ");
  if (!description) return { cell, page: line, description, differs: true, text: line };
  const differs = normalizeIdentifier3(description) !== normalizeIdentifier3(line);
  const text = differs ? `${description}${CONFLICT_SEPARATOR}${line}` : description;
  return { cell, page: line, description, differs, text };
}
function alignedCells(lines) {
  const groups = [];
  for (const line of lines) {
    const current = groups[groups.length - 1];
    if (current && current[0].cell === line.cell) current.push(line);
    else groups.push([line]);
  }
  return groups.map((group) => ({ cell: group[0].cell, lines: group, differs: group.some((line) => line.differs) }));
}
function tradesText(cells) {
  return cells.some((cell, i) => {
    const next = cells[i + 1];
    return !!next && cell.differs && next.differs && (holdsLineOf(cell, next) || holdsLineOf(next, cell));
  });
}
function holdsLineOf(cell, other) {
  const own = new Set(cell.lines.map((line) => normalizeIdentifier3(line.page)));
  const words = foldedWords(cell.lines.map((line) => line.description).join(" "));
  return other.lines.some((line) => {
    const needle = foldedWords(line.page);
    return needle.length > 0 && !own.has(normalizeIdentifier3(line.page)) && containsRun(words, needle);
  });
}
function containsRun(words, run) {
  return words.some((_, i) => run.every((word, k) => words[i + k] === word));
}
function pageTableMarkdown(table) {
  const rows = tableRows(table, sourceCellText);
  return rows.length ? [toMarkdown(rows)] : [];
}
function tableRows(table, textOf) {
  const placed = placeCells(table);
  const width = Math.max(0, ...placed.map(({ cell, column }) => column + cell.colspan));
  const grid = table.map(() => Array.from({ length: width }, () => ""));
  for (const { cell, row, column } of placed) {
    const text = textOf(cell).replace(/\|/g, "\\|");
    for (let r = row; r < Math.min(row + cell.rowspan, grid.length); r++) grid[r][column] = text;
  }
  const used = grid[0]?.map((_, column) => grid.some((cells) => cells[column])) ?? [];
  return grid.filter((cells) => cells.some(Boolean)).map((cells) => cells.filter((_, column) => used[column]));
}
function placeCells(table) {
  const placed = [];
  const occupiedUntil = /* @__PURE__ */ new Map();
  table.forEach((cells, row) => {
    let column = 0;
    for (const cell of cells) {
      while ((occupiedUntil.get(column) ?? -1) >= row) column++;
      placed.push({ cell, row, column });
      for (let c = column; c < column + cell.colspan; c++) occupiedUntil.set(c, row + cell.rowspan - 1);
      column += cell.colspan;
    }
  });
  return placed;
}
function toMarkdown(rows) {
  const line = (cells) => `| ${cells.join(" | ")} |`;
  return [line(rows[0]), line(rows[0].map(() => "---")), ...rows.slice(1).map(line)].join("\n");
}
function splice(description, splices) {
  const parts = [];
  let last = 0;
  for (const { start, end, markdown } of splices) {
    parts.push(description.slice(last, start).trim(), markdown);
    last = end;
  }
  parts.push(description.slice(last).trim());
  return parts.filter(Boolean).join("\n\n");
}
function keepsEveryWord(before, after) {
  const remaining = foldedWords(after)[Symbol.iterator]();
  return foldedWords(before).every((word) => {
    for (let next = remaining.next(); !next.done; next = remaining.next()) {
      if (next.value === word) return true;
    }
    return false;
  });
}
function foldedWords(text) {
  return text.split(/\s+/).map(normalizeIdentifier3).filter((word) => word.length > 0);
}
function sourceCellText(cell) {
  return cellLines(cell).join(CELL_LINE_SEPARATOR);
}
function cellLines(cell) {
  return cell.lines.map(normalize).filter(Boolean);
}
function normalize(value) {
  return normalizeWithIndex(value).text.trim();
}
function normalizeWithIndex(source) {
  let text = "";
  const index = [];
  for (let i = 0; i < source.length; i++) {
    const ch = LOOK_ALIKES[source[i]] ?? source[i];
    if (!ch || ch === " " && (text === "" || text.endsWith(" "))) continue;
    text += ch;
    index.push(i);
  }
  return { text, index };
}
var LOOK_ALIKES = {
  "\xA0": " ",
  "\u2007": " ",
  "\u2009": " ",
  "\u200A": " ",
  "\u202F": " ",
  "	": " ",
  "\r": " ",
  "\n": " ",
  "\u2010": "-",
  "\u2011": "-",
  "\u2012": "-",
  "\u2013": "-",
  "\u2014": "-",
  "\u2212": "-",
  "\u2018": "'",
  "\u2019": "'",
  "\u201A": "'",
  "\u2032": "'",
  "\u201C": '"',
  "\u201D": '"',
  "\u201E": '"',
  "\u2033": '"',
  "\u200B": "",
  "\u200C": "",
  "\u200D": "",
  "\uFEFF": ""
};
function linesOf(noticeText, range) {
  const lines = [];
  let start = range.start;
  while (start < range.end) {
    const newline = noticeText.indexOf("\n", start);
    const end = newline < 0 || newline > range.end ? range.end : newline;
    lines.push({ start, end, text: noticeText.slice(start, end) });
    start = end + 1;
  }
  return lines;
}
var TABLE_LINE = /^\s*\|.*\|\s*$/;
var SEPARATOR_LINE = /^\s*\|(\s*:?-{3,}:?\s*\|)+\s*$/;
function tableLinesAt(lines, i) {
  if (!TABLE_LINE.test(lines[i].text) || !SEPARATOR_LINE.test(lines[i + 1]?.text ?? "")) return void 0;
  let end = i + 2;
  while (end < lines.length && TABLE_LINE.test(lines[end].text)) end++;
  return lines.slice(i, end);
}
function pushText(blocks, noticeText, start, end) {
  const range = trimmed(noticeText, start, end);
  if (range.end > range.start) blocks.push({ kind: "text", ...range });
}
function cellRanges(noticeText) {
  return (line) => {
    const pipes = [];
    for (let at = line.start; at < line.end; at++) {
      if (noticeText[at] === "|" && noticeText[at - 1] !== "\\") pipes.push(at);
    }
    return pipes.slice(1).map((pipe, i) => trimmed(noticeText, pipes[i] + 1, pipe));
  };
}
function trimmed(noticeText, start, end) {
  let from = start;
  let to = end;
  while (from < to && /\s/.test(noticeText[from])) from++;
  while (to > from && /\s/.test(noticeText[to - 1])) to--;
  return { start: from, end: to };
}

// src/display.ts
function toRecallDisplay(recall, rules, { scope, prefixMatch } = {}) {
  const description = noticeDescription(recall.noticeText);
  return {
    title: recall.title,
    ...recall.classification?.headline ? { headline: recall.classification.headline } : {},
    ...recall.hazard ? { hazard: recall.hazard } : {},
    ...recall.remedy ? { remedy: recall.remedy } : {},
    ...rules?.whereToFindIdentifiers ? { whereToFind: rules.whereToFindIdentifiers } : {},
    sourceUrl: recall.sourceUrl,
    ...recall.consumerContact ? { contact: recall.consumerContact } : {},
    checks: [...prefixMatch ? [prefixMatchLine(prefixMatch)] : [], ...rules ? checkLines(rules, scope) : []],
    ...description ? { description } : {},
    ...recall.soldAt ? { soldAt: recall.soldAt } : {},
    ...recall.additionalGuidance ? { additionalGuidance: recall.additionalGuidance } : {},
    images: (recall.images ?? []).map(shownImage)
  };
}
function prefixMatchLine({ listed, model }) {
  return `The notice lists model ${listed}, and your model number ${model} starts with it. Compare your nameplate with the notice to confirm it's your unit.`;
}
function shownImage({ url, caption }) {
  return caption ? { url, caption } : { url };
}
function noticeDescription(noticeText) {
  const section = noticeSections(noticeText).find((s) => s.key === "DESCRIPTION");
  if (!section) return void 0;
  const slice = (range) => noticeText.slice(range.start, range.end);
  const blocks = noticeBlocks(noticeText, section).map(
    (block) => block.kind === "text" ? slice(block) : tableLines(block.rows.map((row) => row.map((cell) => slice(cell).replace(/\\\|/g, "|"))))
  );
  return blocks.join("\n\n").trim() || void 0;
}
function tableLines(rows) {
  const [first = [], ...rest] = rows;
  const header = first.some((cell) => /\d/.test(cell)) ? [] : first;
  const body = header.length ? rest : rows;
  const labelled = (value, column) => header[column] ? `${header[column]}: ${value}` : value;
  return body.map((row) => row.flatMap((value, column) => value ? [labelled(value, column)] : []).join("; ")).join("\n");
}
function checkLines(rules, scope) {
  const checks = applicableChecks(rules, scope);
  const lines = checks.map((check) => check.text);
  const serialRules = scopedSerialRules(rules, scope);
  if (serialRules.length === 0 || checks.some((check) => check.kind === "serial")) return lines;
  const active = serialRules.filter(isRuleActive);
  const serial = active.length ? `Serial numbers ${active.map(describeSerialRule).join(" or ")}` : "Serial number, as the notice states it";
  return [...lines, serial];
}
function describeSerialRule(rule) {
  switch (rule.kind) {
    case "prefix":
      return `starting with ${rule.value}`;
    case "range":
      return rule.value.replace("..", " through ");
    case "exact":
      return rule.value;
  }
}

// src/evaluateUnit.ts
import { typeAffinity } from "@rock/util";

// src/serialRules.ts
function serialRuleMatches(rule, serial) {
  switch (rule.kind) {
    case "prefix":
      return (maskedSerialEnds(rule.value, "drop") ?? []).map((prefix) => prefix.replace(/(?<=.)XX+$/, "")).some((prefix) => !!prefix && serial.startsWith(prefix));
    case "exact":
      return rule.value.split(/\s+\/\s+/).some((value) => (maskedSerialEnds(value) ?? []).some((exact) => exactMatches(serial, exact)));
    case "range":
      return false;
  }
}
function serialRuleCanName(rule) {
  if (rule.kind === "range") return false;
  const values = rule.kind === "exact" ? rule.value.split(/\s+\/\s+/) : [rule.value];
  return values.some((value) => !!maskedSerialEnds(value, "drop")?.length);
}
function exactMatches(serial, exact) {
  const fixed = maskStart(exact);
  return fixed === void 0 ? serial === exact : serial.startsWith(exact.slice(0, fixed));
}

// src/evaluateUnit.ts
function evaluateUnit({ recall, match, unit, families }) {
  const evidence = {
    rule: match.rule,
    strength: match.strength,
    ...match.prefixMatch ? { prefixMatch: match.prefixMatch } : {}
  };
  const prefixOnly = !!match.prefixMatch;
  if (unit.isCustomType) {
    if (match.strength !== "strong" && !unit.typeConfirmed)
      return { verdict: "possibly-affected", needs: ["type"], evidence };
  } else if (recall.typeIds.length === 0) {
    if (match.strength !== "strong") return void 0;
  } else {
    const affinity = typeAffinity(unit.typeId, recall.typeIds, families);
    if (affinity !== "type") {
      if (!affinity && match.strength !== "strong") return void 0;
      evidence.typeMismatch = true;
      if (affinity === "family") evidence.typeFamily = true;
      if (!unit.typeConfirmed) return { verdict: "possibly-affected", needs: ["type"], evidence };
    }
  }
  const scope = unitScope(match.rule, unit.modelNumbers);
  const serialRules = scopedSerialRules(recall.rules, scope);
  const checks = applicableChecks(recall.rules, scope);
  if (serialRules.length === 0 && checks.length === 0)
    return { verdict: prefixOnly ? "possibly-affected" : "affected", needs: [], evidence };
  const active = serialRules.filter(isRuleActive);
  const serialOnly = checks.every((check) => check.kind === "serial");
  const serial = unit.serial ? foldIdentifier(unit.serial) : void 0;
  const named = serial ? active.find((rule) => serialRuleMatches(rule, serial)) : void 0;
  if (named) {
    evidence.serialRule = {
      appliesToModels: named.appliesToModels,
      kind: named.kind,
      value: named.value,
      note: named.note
    };
    if (serialOnly && !prefixOnly) return { verdict: "affected", needs: [], evidence };
  }
  const confirmable = active.some(serialRuleCanName);
  const needs = confirmable && serialOnly && !prefixOnly && !serial ? ["serial"] : [];
  return { verdict: "possibly-affected", needs, evidence };
}

// src/liveFacts.ts
function liveFactsChanged(before, after) {
  const facts = (recall) => JSON.stringify([
    recall.rules,
    recall.brandIds,
    recall.classification?.typeIds ?? [],
    recall.classification?.topicIds ?? [],
    toRecallDisplay(recall, recall.rules)
  ]);
  return facts(before) !== facts(after);
}

// src/matchModel.ts
import { typeAffinity as typeAffinity2 } from "@rock/util";
function matchableModel(model) {
  return { brandId: model.brandId, modelCanonical: model.modelCanonical, spellings: model.spellings ?? [] };
}
function recallCheckKey(model) {
  const { brandId, modelCanonical, spellings } = matchableModel(model);
  return JSON.stringify([brandId ?? null, modelCanonical, [...spellings].sort()]);
}
function modelNumbersOf(model) {
  return [model.modelCanonical];
}
function matchModel(recall, model) {
  if (!model.brandId || !recall.brandIds.includes(model.brandId) || !recall.rules) return void 0;
  const candidates = matchCandidates(model);
  if (candidates.length === 0) return void 0;
  let best;
  for (const rule of recall.rules.modelRules) {
    if (!isRuleActive(rule)) continue;
    const hit = namedHit(rule, candidates) ?? prefixHit(rule, candidates);
    if (!hit) continue;
    if (hit.rank === 0) return hit.match;
    if (!best || outranks(hit, best)) best = hit;
  }
  return best?.match;
}
function matchUnmodeledUnit(recall, unit, families) {
  if (!unit.brandId || !recall.brandIds.includes(unit.brandId)) return void 0;
  if (unit.isCustomType || !typeAffinity2(unit.typeId, recall.typeIds, families)) return void 0;
  const rule = everyModelRule(recall.rules);
  return rule ? { rule: toPlainRule(rule), strength: "weak" } : void 0;
}
function isUnmodeledUnit(unit) {
  return !unit.details?.modelId && !unit.fields?.modelNumber?.trim() && !unit.fields?.productName?.trim();
}
function everyModelRule(rules) {
  return rules?.modelRules.find((rule) => rule.kind === "any" && isRuleActive(rule));
}
function recallReachesNobody(recall) {
  if (recall.topicIds.length) return void 0;
  if (!recall.rules?.modelRules.some(isRuleActive)) return "no-model-rule";
  if (recall.brandIds.length === 0) return "no-brand";
  return void 0;
}
function matchCandidates(model) {
  const candidates = [];
  const add = (raw, canonical) => {
    const folded = foldIdentifier(raw);
    if (folded && !candidates.some((c) => c.folded === folded)) candidates.push({ raw, folded, canonical });
  };
  add(model.modelCanonical, true);
  for (const spelling of model.spellings ?? []) add(spelling, false);
  return candidates;
}
function namedHit(rule, candidates) {
  const named = candidates.find((candidate) => ruleNames(rule, candidate.folded));
  if (!named) return void 0;
  const match = { rule: toPlainRule(rule), strength: identifierStrength(rule, named.folded) };
  return { match, rank: rankOf(match, named.canonical) };
}
function prefixHit(rule, candidates) {
  for (const candidate of candidates) {
    const listed = listedPrefixOf(rule, candidate.folded);
    if (listed === void 0) continue;
    const match = {
      rule: toPlainRule(rule),
      strength: "weak",
      prefixMatch: { listed, model: candidate.raw }
    };
    return { match, rank: rankOf(match, candidate.canonical) };
  }
  return void 0;
}
function outranks(hit, best) {
  if (hit.rank !== best.rank) return hit.rank < best.rank;
  return listedLength(hit.match) > listedLength(best.match);
}
function listedLength(match) {
  return match.prefixMatch ? foldIdentifier(match.prefixMatch.listed).length : 0;
}
function rankOf(match, byCanonical) {
  if (match.rule.kind === "any") return 6;
  const base = match.prefixMatch ? 4 : match.strength === "strong" ? 0 : 2;
  return base + (byCanonical ? 0 : 1);
}
function toPlainRule(rule) {
  return { kind: rule.kind, value: rule.value, note: rule.note };
}

// src/merge.ts
import { normalizeBrandName } from "@rock/util";
var EXTRACTOR_ROLES = ["sol", "sonnet"];
var SCOPE_BREADTH = {
  "product-line-no-model-numbers": 3,
  "all-units-of-listed-models": 2,
  "listed-models-with-conditions": 1,
  unclear: 0
};
function mergeExtractions(extractions, prior) {
  const { sol, sonnet } = extractions;
  const suppressed = prior?.suppressed ?? [];
  const isSuppressed = (value) => suppressed.includes(foldIdentifier(value));
  const modelRules = unionRules(
    sol.modelRules,
    sonnet.modelRules,
    (rule) => `${rule.kind}:${foldIdentifier(rule.value)}`,
    prior?.modelRules ?? []
  ).filter((rule) => !isSuppressed(rule.value));
  const serialRules = unionRules(sol.serialRules, sonnet.serialRules, serialRuleKey, prior?.serialRules ?? []).filter(
    (rule) => !isSuppressed(rule.value)
  );
  const scope = SCOPE_BREADTH[sol.scope] >= SCOPE_BREADTH[sonnet.scope] ? sol.scope : sonnet.scope;
  return {
    brands: unionValues(sol.brands, sonnet.brands, (b) => normalizeBrandName(b).key, prior?.brands ?? []),
    scope,
    ...sol.scope !== sonnet.scope ? { scopeDiffers: true } : {},
    modelRules,
    serialRules,
    checks: prior?.checksAdminEdited ? prior.checks : mergeChecks(sol.checks, sonnet.checks),
    ...prior?.checksAdminEdited ? { checksAdminEdited: true } : {},
    upcs: unionValues(sol.upcs, sonnet.upcs, foldIdentifier, prior?.upcs ?? []),
    whereToFindIdentifiers: sol.whereToFindIdentifiers,
    confidence: Math.min(sol.confidence, sonnet.confidence),
    suppressed
  };
}
function unionRules(fromSol, fromSonnet, keyOf, prior) {
  const acceptedKeys = new Set(prior.filter((rule) => rule.accepted).map(keyOf));
  const out = /* @__PURE__ */ new Map();
  for (const rule of fromSol) out.set(keyOf(rule), { ...rule, source: "sol", verified: false });
  for (const rule of fromSonnet) {
    const key = keyOf(rule);
    const existing = out.get(key);
    out.set(key, existing ? { ...existing, source: "both" } : { ...rule, source: "sonnet", verified: false });
  }
  for (const rule of prior) {
    if (rule.source === "admin" && !out.has(keyOf(rule))) out.set(keyOf(rule), rule);
  }
  return [...out.values()].map((rule) => acceptedKeys.has(keyOf(rule)) ? { ...rule, accepted: true } : rule);
}
function serialRuleKey(rule) {
  return `${rule.kind}:${foldIdentifier(rule.value)}:${modelsKey(rule)}`;
}
function modelsKey(rule) {
  return rule.appliesToModels.map(foldIdentifier).join(",");
}
function unionValues(fromSol, fromSonnet, keyOf, prior) {
  const out = /* @__PURE__ */ new Map();
  for (const value of fromSol) if (keyOf(value)) out.set(keyOf(value), { value, source: "sol" });
  for (const value of fromSonnet) {
    const key = keyOf(value);
    if (!key) continue;
    const existing = out.get(key);
    out.set(key, existing ? { ...existing, source: "both" } : { value, source: "sonnet" });
  }
  for (const entry of prior) {
    if (entry.source === "admin" && !out.has(keyOf(entry.value))) out.set(keyOf(entry.value), entry);
  }
  return [...out.values()];
}
function mergeChecks(fromSol, fromSonnet) {
  const covered = (check) => fromSol.some((sol) => sol.kind === check.kind && coversModels(sol.appliesToModels, check.appliesToModels));
  return [
    ...fromSol.map((check) => ({ ...check, source: "sol" })),
    ...fromSonnet.filter((check) => !covered(check)).map((check) => ({ ...check, source: "sonnet" }))
  ];
}
function coversModels(scope, other) {
  if (scope.length === 0) return true;
  const folded = new Set(scope.map(foldIdentifier));
  return other.length > 0 && other.every((model) => folded.has(foldIdentifier(model)));
}
function acceptRule(rules, field, index) {
  return withRuleAt(rules, field, index, (rule) => ({ ...rule, accepted: true }));
}
function addModelRule(rules, value, kind) {
  const folded = foldIdentifier(value);
  if (!folded) return rules;
  const exists = rules.modelRules.some((rule) => rule.kind === kind && foldIdentifier(rule.value) === folded);
  if (exists) return rules;
  return {
    ...rules,
    modelRules: [...rules.modelRules, adminModelRule(kind, value.trim())],
    suppressed: rules.suppressed.filter((s) => s !== folded)
  };
}
function addEveryModelRule(rules) {
  if (rules.modelRules.some((rule) => rule.kind === "any")) return rules;
  return { ...rules, modelRules: [...rules.modelRules, adminModelRule("any", "*")] };
}
function adminModelRule(kind, value) {
  return { kind, value, note: null, source: "admin", verified: false, accepted: true };
}
function suppressRule(rules, field, index) {
  const rule = rules[field][index];
  if (!rule) return rules;
  const folded = foldIdentifier(rule.value);
  const suppressed = folded ? [.../* @__PURE__ */ new Set([...rules.suppressed, folded])] : rules.suppressed;
  const next = { ...rules, suppressed };
  if (field === "modelRules") {
    return { ...next, modelRules: rules.modelRules.filter((_, i) => i !== index) };
  }
  return { ...next, serialRules: rules.serialRules.filter((_, i) => i !== index) };
}
function addCheck(rules, edit) {
  return { ...rules, checks: [...rules.checks, { ...edit, source: "admin" }], checksAdminEdited: true };
}
function replaceCheck(rules, index, edit) {
  const checks = rules.checks.map((existing, i) => i === index ? { ...edit, source: "admin" } : existing);
  return { ...rules, checks, checksAdminEdited: true };
}
function removeCheck(rules, index) {
  return { ...rules, checks: rules.checks.filter((_, i) => i !== index), checksAdminEdited: true };
}
function withRuleAt(rules, field, index, update) {
  if (field === "modelRules") {
    return { ...rules, modelRules: rules.modelRules.map((rule, i) => i === index ? update(rule) : rule) };
  }
  return { ...rules, serialRules: rules.serialRules.map((rule, i) => i === index ? update(rule) : rule) };
}

// src/stuck.ts
var RECALL_STUCK_AFTER_MS = 60 * 60 * 1e3;
function isRecallStuck(recall, now) {
  const shortOfReview = recall.status === "new" || recall.status === "classified" && recall.typed;
  return shortOfReview && now - Date.parse(recall.updatedAt) > RECALL_STUCK_AFTER_MS;
}

// src/topics.ts
import { addIntervalToLocalDate, dateToLocalDate } from "@rock/util";
var RECALL_TOPIC_LOOKBACK_MONTHS = 12;
var RECALL_TOPIC_FOLLOW_MAX_YEARS = 10;
var RECALL_TOPIC_FOLLOW_STEP_MONTHS = 3;
function topicLookbackStart(now) {
  return monthsBefore(now, RECALL_TOPIC_LOOKBACK_MONTHS);
}
function topicFollowFloor(now) {
  return monthsBefore(now, RECALL_TOPIC_FOLLOW_MAX_YEARS * 12);
}
function followStart(window, now) {
  return window === "lookback" ? topicLookbackStart(now) : monthsBefore(now, RECALL_TOPIC_FOLLOW_STEP_MONTHS);
}
function goBackStart(since, now) {
  if (!canGoBack(since, now)) return since;
  const earlier = addIntervalToLocalDate(since, { value: -RECALL_TOPIC_FOLLOW_STEP_MONTHS, unit: "months" });
  const floor = topicFollowFloor(now);
  return earlier < floor ? floor : earlier;
}
function canGoBack(since, now) {
  return since > topicFollowFloor(now);
}
function followReaches(follows, recall, now) {
  if (!inTopicReach(recall, now)) return false;
  return recall.topicIds.some((id) => {
    const follow = follows?.[id];
    return !!follow && recall.publishedAt >= follow.since;
  });
}
function inTopicReach(recall, now) {
  return recall.publishedAt >= topicFollowFloor(now);
}
function sharesTopic(a, b) {
  return b.some((id) => a?.includes(id));
}
function hasFyiAnswer(verdict) {
  return !!verdict.acceptedAt || !!verdict.resolution;
}
function topicApprovalBlock(recall) {
  if (recall.status !== "classified" && recall.status !== "draft") return "not-awaiting";
  if (!recall.classification?.topicIds.length) return "no-topics";
  const rules = recall.draftRules ?? recall.rules;
  if (rules?.modelRules.some(isRuleActive)) return "has-model-rules";
  if (recall.typed && !rules) return "awaiting-rules";
  return void 0;
}
function monthsBefore(now, months) {
  return addIntervalToLocalDate(dateToLocalDate(now), { value: -months, unit: "months" });
}

// src/versions.ts
var RECALL_CLASSIFY_VERSION = "classify-4";
var RECALL_RULES_VERSION = "rules-4";

// src/verdictStatus.ts
import { isQuietFyi } from "@rock/types";
var PENDING = { kind: "possibly-affected", label: "Pending", muted: false, tone: "warning" };
var AFFECTED = { kind: "affected", label: "Affected", muted: false, tone: "danger" };
var WITHDRAWN = { kind: "withdrawn", label: "Withdrawn", muted: true, tone: "muted" };
var FYI = { kind: "fyi", label: "FYI", muted: false, tone: "info" };
var RESOLUTION_LABELS = {
  handled: "Done",
  "not-my-unit": "N/A",
  dismissed: "Dismissed"
};
var RESOLUTION_TONES = {
  handled: "success",
  "not-my-unit": "muted",
  dismissed: "muted"
};
function recallVerdictStatus(verdict) {
  if (verdict.withdrawn) return WITHDRAWN;
  if (verdict.resolution) return resolvedStatus(verdict.resolution.outcome);
  if (verdict.acceptedAt) return AFFECTED;
  switch (verdict.verdict) {
    case "affected":
      return AFFECTED;
    case "possibly-affected":
      return PENDING;
    case "fyi":
      return FYI;
  }
}
function isUnansweredFyi(verdict) {
  return recallVerdictStatus(verdict).kind === "fyi";
}
var RECALL_STATUS_GUIDE = [
  { status: PENDING, description: "This recall may include your unit. Check yours against the notice." },
  {
    status: FYI,
    description: "A recall in a topic you follow. Check whether you own one."
  },
  {
    status: AFFECTED,
    description: "This recall covers what you own. Follow the notice to get it fixed or replaced."
  },
  { status: resolvedStatus("handled"), description: "You've taken care of it." },
  { status: resolvedStatus("not-my-unit"), description: "You checked, and it doesn't apply to your unit." },
  { status: WITHDRAWN, description: "The recall changed or was withdrawn, and no longer covers your unit." }
];
function resolutionLabel(outcome) {
  return RESOLUTION_LABELS[outcome];
}
function sortRecallVerdicts(verdicts) {
  return [...verdicts].sort((a, b) => {
    const rank = statusRank(recallVerdictStatus(a)) - statusRank(recallVerdictStatus(b));
    return rank !== 0 ? rank : a.display.title.localeCompare(b.display.title);
  });
}
function withdrawnReasonText(reason) {
  switch (reason) {
    case "rejected":
      return "The recall was withdrawn from our list.";
    case "rules-narrowed":
      return "The recall was narrowed to fewer units, and yours is outside it now.";
    case "unit-changed":
      return "Your equipment's details changed, and the recall no longer applies to it.";
    case "home-changed":
      return "You stopped following its topic, or it now shows on your equipment.";
  }
}
function resolutionText(outcome, at) {
  const date = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(
    new Date(at)
  );
  switch (outcome) {
    case "handled":
      return `You marked this done on ${date}`;
    case "not-my-unit":
      return `You said this doesn't apply on ${date}`;
    case "dismissed":
      return `You dismissed this on ${date}`;
  }
}
function resolvedStatus(outcome) {
  return { kind: "resolved", label: RESOLUTION_LABELS[outcome], muted: true, tone: RESOLUTION_TONES[outcome] };
}
function statusRank(status) {
  if (status.kind === "affected") return 0;
  if (status.kind === "possibly-affected" || status.kind === "fyi") return 1;
  return 2;
}
export {
  CELL_LINE_SEPARATOR,
  CONFLICT_SEPARATOR,
  EXTRACTOR_ROLES,
  MAX_TOKEN_RUN,
  NOTICE_SECTION_KEYS,
  RECALL_CLASSIFY_VERSION,
  RECALL_RULES_VERSION,
  RECALL_STATUS_GUIDE,
  RECALL_STUCK_AFTER_MS,
  RECALL_TOPIC_FOLLOW_MAX_YEARS,
  RECALL_TOPIC_FOLLOW_STEP_MONTHS,
  RECALL_TOPIC_LOOKBACK_MONTHS,
  acceptRule,
  addCheck,
  addEveryModelRule,
  addModelRule,
  applicableChecks,
  canGoBack,
  catalogVersionOf,
  checkNumbers,
  conditionBinds,
  describeMatchedRule,
  describeModelRule,
  evaluateUnit,
  everyModelRule,
  exactIdentifiers,
  foldIdentifier,
  followReaches,
  followStart,
  formatNotice,
  goBackStart,
  harvestIdentifierTokens,
  hasFyiAnswer,
  identifierInNotice,
  identifierStrength,
  inTopicReach,
  isBetween,
  isQuietFyi,
  isRecallStuck,
  isRuleActive,
  isUnansweredFyi,
  isUnmodeledUnit,
  listedPrefixOf,
  liveFactsChanged,
  locateIdentifier,
  maskStart,
  maskedSerialEnds,
  matchModel,
  matchUnmodeledUnit,
  matchableModel,
  mergeExtractions,
  modelNumbersOf,
  modelRuleIdentifiers,
  namesModel,
  normalizeProductKind,
  noticeBlocks,
  noticeSections,
  placeTables,
  planClassificationEdit,
  planRulingLift,
  planTopicRuleOut,
  planTopicRulingLift,
  planTopicsEdit,
  planTypeRuleOut,
  recallCatalogTypes,
  recallCatalogVersion,
  recallCheckKey,
  recallCoverageGap,
  recallCoverageOf,
  recallCoverageTier,
  recallReachesNobody,
  recallVerdictStatus,
  reconcileClassification,
  removeCheck,
  replaceCheck,
  resolutionLabel,
  resolutionText,
  ruleEnds,
  ruleNames,
  scopeCovers,
  scopeValues,
  scopedSerialRules,
  serialEnds,
  serialRuleCanName,
  serialRuleMatches,
  sharesTopic,
  sortRecallVerdicts,
  suppressRule,
  suppressedInNotice,
  toRecallDisplay,
  tokenizeNotice,
  topicApprovalBlock,
  topicFollowFloor,
  topicLookbackStart,
  unitScope,
  verifyRecallRules,
  withdrawnReasonText
};
//# sourceMappingURL=index.js.map